export interface ValidationResult {
  isValid: boolean
  error?: string
}

export const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
} as const

export const MAX_FILE_SIZE = {
  pdf: 10 * 1024 * 1024, // 10MB for PDFs
  image: 5 * 1024 * 1024, // 5MB for images
} as const

export const validateFileType = (file: File): ValidationResult => {
  const acceptedTypes = Object.keys(ACCEPTED_FILE_TYPES)
  
  if (!acceptedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Accepted types: PDF, JPG, PNG, WebP`,
    }
  }
  
  return { isValid: true }
}

export const validateFileSize = (file: File): ValidationResult => {
  const isPdf = file.type === 'application/pdf'
  const maxSize = isPdf ? MAX_FILE_SIZE.pdf : MAX_FILE_SIZE.image
  const maxSizeMB = isPdf ? 10 : 5
  
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `File size exceeds limit (${maxSizeMB}MB). Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
    }
  }
  
  return { isValid: true }
}

export const validateFileName = (file: File): ValidationResult => {
  const fileName = file.name
  
  // Check for empty file name
  if (!fileName || fileName.trim().length === 0) {
    return {
      isValid: false,
      error: 'File name cannot be empty',
    }
  }
  
  // Check for invalid characters (security)
  // eslint-disable-next-line no-control-regex -- control characters are exactly what we want to reject
  const invalidChars = /[<>:"|?*\x00-\x1f]/
  if (invalidChars.test(fileName)) {
    return {
      isValid: false,
      error: 'File name contains invalid characters',
    }
  }
  
  // Check file extension
  const hasValidExtension = Object.values(ACCEPTED_FILE_TYPES)
    .flat()
    .some(ext => fileName.toLowerCase().endsWith(ext))
    
  if (!hasValidExtension) {
    return {
      isValid: false,
      error: 'File must have a valid extension (.pdf, .jpg, .jpeg, .png, .webp)',
    }
  }
  
  return { isValid: true }
}

export const validateFile = async (file: File): Promise<ValidationResult> => {
  // Type validation
  const typeValidation = validateFileType(file)
  if (!typeValidation.isValid) {
    return typeValidation
  }
  
  // Size validation
  const sizeValidation = validateFileSize(file)
  if (!sizeValidation.isValid) {
    return sizeValidation
  }
  
  // Name validation
  const nameValidation = validateFileName(file)
  if (!nameValidation.isValid) {
    return nameValidation
  }
  
  // Additional PDF validation
  if (file.type === 'application/pdf') {
    try {
      // Read first few bytes to verify PDF header
      const buffer = await file.slice(0, 5).arrayBuffer()
      const header = new TextDecoder().decode(buffer)
      
      if (!header.startsWith('%PDF')) {
        return {
          isValid: false,
          error: 'Invalid PDF file format',
        }
      }
    } catch {
      return {
        isValid: false,
        error: 'Unable to read file',
      }
    }
  }
  
  return { isValid: true }
}